import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { authService } from "@/services/auth.service";
import { supabase } from "@/integrations/supabase/client";
import { sessionManager } from "@/utils/sessionManager";
import useSessionWithRetry from "./useSessionWithRetry";

type AuthFormData = {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  termsAccepted?: boolean;
};

export const useAuth = (selectedRole: "client" | "therapist" | null, isLogin: boolean) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { session } = useSessionWithRetry();
  const navigate = useNavigate();

  const handleAuthSubmit = async (formData: AuthFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    let timeoutId: NodeJS.Timeout;
    
    try {
      // Set a shorter timeout (15 seconds instead of 30)
      timeoutId = setTimeout(() => {
        console.log("Auth operation timed out");
        setIsLoading(false);
        setErrorMessage("Operation timed out. Please try again.");
      }, 15000);

      if (isLogin) {
        // Login flow
        console.log("Attempting login with role:", selectedRole);
        const { user, userRole } = await authService.signIn(
          formData.email,
          formData.password,
          selectedRole
        );

        if (user) {
          setUserId(user.id);
          
          // Check for saved redirect path
          const savedRedirectPath = localStorage.getItem('redirectAfterLogin');
          if (savedRedirectPath) {
            console.log(`Redirecting to saved path: ${savedRedirectPath}`);
            localStorage.removeItem('redirectAfterLogin');
            navigate(savedRedirectPath);
          } else if (userRole === 'client') {
            console.log("Client login detected, redirecting to journal");
            navigate('/journal');
          } else if (userRole === 'therapist') {
            console.log("Redirecting to therapist dashboard");
            navigate('/therapist');
          } else if (userRole === 'admin') {
            console.log("Redirecting to admin dashboard");
            navigate('/admin');
          }
        }
      } else {
        // Registration flow
        console.log("Attempting registration with selected role:", selectedRole);
        if (!formData.firstName || !formData.lastName) {
          throw new Error("Please provide both first and last name for registration.");
        }

        if (!formData.termsAccepted) {
          throw new Error("You must accept the Terms and Conditions to register.");
        }

        if (selectedRole === "therapist") {
          console.log("Processing therapist registration");
          const { user } = await authService.signUp(
            {
              email: formData.email,
              password: formData.password,
              firstName: formData.firstName,
              lastName: formData.lastName,
              company: formData.company,
              termsAccepted: formData.termsAccepted
            }, 
            false
          );
          
          if (user) {
            setUserId(user.id);
            await authService.setUserRole(user.id, 'therapist');
            toast.success("Therapist registration submitted successfully!");
            try {
              await supabase.auth.signOut();
            } catch (logoutError) {
              console.error("Error during logout:", logoutError);
            }
            navigate('/therapist-approval');
          } else {
            toast.success("Registration in process. Please check your email to verify your account.");
          }
        } else {
          // Client registration
          console.log("Processing client registration");
          const { user } = await authService.signUp(
            {
              email: formData.email,
              password: formData.password,
              firstName: formData.firstName,
              lastName: formData.lastName,
              termsAccepted: formData.termsAccepted
            },
            true
          );
          
          if (user) {
            setUserId(user.id);
            await authService.setUserRole(user.id, 'client');
            toast.success("Account created successfully!");
            navigate('/journal');
          } else {
            toast.success("Registration in process. Please check your email to verify your account.");
          }
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      setErrorMessage(error instanceof Error ? error.message : "An error occurred during authentication.");
      toast.error(error instanceof Error ? error.message : "Authentication failed");
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Auto-redirect if already authenticated with a valid session
    if (session && session.user) {
      console.log("User already authenticated, checking role");
      
      const checkRoleAndRedirect = async () => {
        try {
          const { data: roleData, error: roleError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (roleError) {
            console.error("Error checking role:", roleError);
            return;
          }

          if (!roleData) {
            console.warn(`No role found for user ${session.user.id}`);
            return;
          }

          console.log(`User role: ${roleData.role}, Selected role: ${selectedRole}`);
          
          // If no role is selected, redirect based on user's role
          if (!selectedRole) {
            const redirectPath = roleData.role === 'client' ? '/journal' : '/therapist';
            console.log(`No role selected, redirecting to ${redirectPath} based on user role`);
            navigate(redirectPath);
            return;
          }
          
          // If selected role matches user's role, redirect
          if (roleData.role === selectedRole) {
            const redirectPath = selectedRole === 'client' ? '/journal' : '/therapist';
            console.log(`Authenticated ${selectedRole} detected, redirecting to ${redirectPath}`);
            navigate(redirectPath);
            return;
          }
          
          // If roles don't match, sign out
          console.warn(`Role mismatch: User is ${roleData.role} but selected ${selectedRole}`);
          await authService.silentSignOut();
          toast.error("Role mismatch detected. Please sign in with the correct role.");
        } catch (error) {
          console.error("Error checking role:", error);
        }
      };
      
      checkRoleAndRedirect();
    }
  }, [session, navigate, selectedRole]);

  return {
    isLoading,
    userId,
    showRoleSelector,
    errorMessage,
    handleAuthSubmit,
    setShowRoleSelector
  };
};
